import React from 'react';
import { 
  Typography, 
  Card, 
  Descriptions, 
  Tag, 
  Divider,
  Alert,
  Upload
} from 'antd';
import { 
  CheckCircleOutlined, 
  UserOutlined,
  CarOutlined,
  EnvironmentOutlined,
  ToolOutlined,
  FileTextOutlined,
  PictureOutlined
} from '@ant-design/icons';

const { Title, Text } = Typography;

/**
 * Summary Step Component
 * Final review of all entered information before submission
 */
const SummaryStep = ({ form, formData, updateFormData, loading }) => {

  /**
   * Format phone number for display
   */
  const formatPhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return 'Not provided';
    return phoneNumber;
  };

  /**
   * Format coordinates for display
   */
  const formatCoordinates = (lat, lng) => {
    if (!lat || !lng) return 'Not provided';
    return `${parseFloat(lat).toFixed(6)}, ${parseFloat(lng).toFixed(6)}`;
  };

  /**
   * Get vehicle summary text
   */
  const getVehicleSummary = () => {
    const parts = [];
    if (formData.truckRegistrationNumber) parts.push(formData.truckRegistrationNumber);
    if (formData.truckMake) parts.push(formData.truckMake);
    if (formData.truckModel) parts.push(formData.truckModel);
    if (formData.truckYear) parts.push(`(${formData.truckYear})`);
    return parts.length > 0 ? parts.join(' ') : 'Not specified';
  };

  /**
   * Get trailer information
   */
  const getTrailerInfo = () => {
    const trailers = [];
    
    if (formData.trailer1Fault) {
      const trailer1Data = formData.trailer1Data || {};
      const trailer1Summary = [
        trailer1Data.registration,
        trailer1Data.make,
        trailer1Data.model
      ].filter(Boolean).join(' ') || 'Details not provided';
      trailers.push(`Trailer 1: ${trailer1Summary}`);
    }
    
    if (formData.trailer2Fault) {
      const trailer2Data = formData.trailer2Data || {};
      const trailer2Summary = [
        trailer2Data.registration,
        trailer2Data.make,
        trailer2Data.model
      ].filter(Boolean).join(' ') || 'Details not provided';
      trailers.push(`Trailer 2: ${trailer2Summary}`);
    }
    
    return trailers.length > 0 ? trailers : ['No trailer faults reported'];
  };

  /**
   * Check if all required fields are completed
   */
  const isFormComplete = () => {
    const requiredFields = [
      'controllerName',
      'controllerContactNo',
      'selectedCompanyId',
      'driverName',
      'driverCell',
      'address',
      'latitude',
      'longitude',
      'truckRegistrationNumber',
      'description'
    ];

    return requiredFields.every(field => {
      const value = formData[field];
      return value && value.toString().trim().length > 0;
    });
  };

  return (
    <div>
      <Title level={3} style={{ marginBottom: '24px' }}>
        <CheckCircleOutlined style={{ marginRight: '8px' }} />
        Review & Submit
      </Title>
      
      <Text type="secondary" style={{ display: 'block', marginBottom: '24px' }}>
        Please review all information before submitting the breakdown request
      </Text>

      {!isFormComplete() && (
        <Alert
          message="Incomplete Information"
          description="Some required fields are missing. Please go back and complete all required information."
          type="warning"
          showIcon
          style={{ marginBottom: '24px' }}
        />
      )}

      {/* Controller Information */}
      <Card 
        title={
          <span>
            <UserOutlined style={{ marginRight: '8px' }} />
            Controller Information
          </span>
        } 
        size="small" 
        style={{ marginBottom: '16px' }}
      >
        <Descriptions column={1} size="small" layout="vertical" className="request-summary">
          <Descriptions.Item label="Controller Name">
            {formData.controllerName || <Text type="secondary">Not provided</Text>}
          </Descriptions.Item>
          <Descriptions.Item label="Contact Number">
            {formatPhoneNumber(formData.controllerContactNo)}
          </Descriptions.Item>
          <Descriptions.Item label="Customer Company" span={2}>
            {formData.selectedCompany?.name || <Text type="secondary">Not selected</Text>}
            {formData.selectedCompany?.uniqueCode && (
              <Tag style={{ marginLeft: '8px' }}>
                Code: {formData.selectedCompany.uniqueCode}
              </Tag>
            )}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* Driver Information */}
      <Card 
        title={
          <span>
            <CarOutlined style={{ marginRight: '8px' }} />
            Driver Information
          </span>
        } 
        size="small" 
        style={{ marginBottom: '16px' }}
      >
        <Descriptions column={1} size="small" layout="vertical" className="request-summary">
          <Descriptions.Item label="Driver Name">
            {formData.driverName || <Text type="secondary">Not provided</Text>}
          </Descriptions.Item>
          <Descriptions.Item label="Contact Number">
            {formatPhoneNumber(formData.driverCell)}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* Location Information */}
      <Card 
        title={
          <span>
            <EnvironmentOutlined style={{ marginRight: '8px' }} />
            Location Information
          </span>
        } 
        size="small" 
        style={{ marginBottom: '16px' }}
      >
        <Descriptions column={1} size="small" layout="vertical" className="request-summary">
          <Descriptions.Item label="Address">
            {formData.address || <Text type="secondary">Not provided</Text>}
          </Descriptions.Item>
          <Descriptions.Item label="Coordinates">
            {formatCoordinates(formData.latitude, formData.longitude)}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* Vehicle Information */}
      <Card 
        title={
          <span>
            <ToolOutlined style={{ marginRight: '8px' }} />
            Vehicle Information
          </span>
        } 
        size="small" 
        style={{ marginBottom: '16px' }}
      >
        <Descriptions column={1} size="small" layout="vertical" className="request-summary">
          <Descriptions.Item label="Registration">
            {formData.truckRegistrationNumber || <Text type="secondary">Not provided</Text>}
          </Descriptions.Item>
          <Descriptions.Item label="Fleet Number">
            {formData.truckFleetNumber || <Text type="secondary">Not provided</Text>}
          </Descriptions.Item>
          <Descriptions.Item label="VIN Number">
            {formData.truckVinNumber || <Text type="secondary">Not provided</Text>}
          </Descriptions.Item>
          <Descriptions.Item label="Vehicle">
            {getVehicleSummary()}
          </Descriptions.Item>
          <Descriptions.Item label="Mileage">
            {formData.mileage || <Text type="secondary">Not provided</Text>}
          </Descriptions.Item>
          <Descriptions.Item label="Lease Type">
            {formData.leaseType || <Text type="secondary">Not provided</Text>}
          </Descriptions.Item>
          <Descriptions.Item label="Cost Center" span={2}>
            {formData.costCentreName || <Text type="secondary">Not provided</Text>}
          </Descriptions.Item>
        </Descriptions>

        {(formData.trailer1Fault || formData.trailer2Fault) && (
          <>
            <Divider style={{ margin: '12px 0' }} />
            <Text strong>Trailer Information:</Text>
            <div style={{ marginTop: '8px' }}>
              {getTrailerInfo().map((trailer, index) => (
                <div key={index} style={{ marginBottom: '4px' }}>
                  <Tag color="orange">{trailer}</Tag>
                </div>
              ))}
            </div>
          </>
        )}
      </Card>

      {/* Order & Fault Details */}
      <Card 
        title={
          <span>
            <FileTextOutlined style={{ marginRight: '8px' }} />
            Order & Fault Details
          </span>
        } 
        size="small"
      >
        <Descriptions column={1} size="small" layout="vertical" className="request-summary">
          <Descriptions.Item label="Order Number">
            {formData.orderNumber || <Text type="secondary">Not provided</Text>}
          </Descriptions.Item>
          <Descriptions.Item label="Internal Case Notes">
            {formData.internalCaseNotes ? (
              <div style={{ 
                background: '#fafafa', 
                padding: '8px', 
                borderRadius: '4px',
                maxHeight: '100px',
                overflowY: 'auto'
              }}>
                {formData.internalCaseNotes}
              </div>
            ) : (
              <Text type="secondary">No internal notes</Text>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Fault Description">
            {formData.description ? (
              <div style={{ 
                background: '#fff7e6', 
                border: '1px solid #ffd591',
                padding: '8px', 
                borderRadius: '4px',
                maxHeight: '120px',
                overflowY: 'auto'
              }}>
                {formData.description}
              </div>
            ) : (
              <Text type="secondary">Not provided</Text>
            )}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* Images (Review) */}
      <Card 
        title={
          <span>
            <PictureOutlined style={{ marginRight: '8px' }} />
            Images
          </span>
        }
        size="small"
        style={{ marginTop: '16px' }}
      >
        {Array.isArray(formData.imageryFiles) && formData.imageryFiles.length > 0 ? (
          <Upload
            listType="picture-card"
            beforeUpload={() => false}
            fileList={formData.imageryFiles}
            showUploadList={{ showRemoveIcon: false, showDownloadIcon: false }}
            disabled
          />
        ) : (
          <Text type="secondary">No images attached</Text>
        )}
      </Card>

      {isFormComplete() && (
        <Alert
          message="Ready to Submit"
          description="All required information has been provided. Click 'Generate Request' to submit the breakdown request."
          type="success"
          showIcon
          style={{ marginTop: '24px' }}
        />
      )}
    </div>
  );
};

export default SummaryStep;
